import { Button, Box, Typography, Dialog } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { USER_DETAILS_NOT_FOUND_ERROR } from 'src/constants/errors';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import ErrorBoundary from '../error-boundary/ErrorBoundry';
import { useNavigate, useLocation } from 'react-router-dom';
import { PATHS } from 'src/constants/navigateRoutes';
import variableStyles from 'src/scss/_variables.scss';
import { PLAN_TYPES } from 'src/constants/cardsEnum';
import { isOrganizationPlanAtLeast } from 'src/utils/organizationBilling';
import {
  customEventTracking,
  CustomEventTrackingPayload,
} from 'src/services/customEventTracking';
import { useStatsigClient } from '@statsig/react-bindings';
import { logStatsigEvent } from 'src/utils/statsigEvent';
import { UpgradeOffer } from 'src/types/upgradeOffer';

interface UpgradePopupProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  upgradeOffer?: UpgradeOffer;
}

const UpgradePopup: React.FC<UpgradePopupProps> = observer(
  ({ isOpen, setIsOpen, upgradeOffer }) => {
    const { getUserDetails } = useAuthStore();
    const { organizationDetails } = useOrganizationStore();
    const userDetails = getUserDetails();
    const navigate = useNavigate();
    const location = useLocation();
    const { client } = useStatsigClient();
    const trimmedPath = location.pathname.substring(1);
    const isMaxPlan = isOrganizationPlanAtLeast(
      organizationDetails,
      PLAN_TYPES.PRO,
    );

    if (!isOpen) {
      return null;
    }

    const handleUpgrade = async () => {
      if (userDetails) {
        const body: CustomEventTrackingPayload = {
          userId: userDetails._id,
          orgId: userDetails.organizationId,
          eventName: `${trimmedPath}_upgrade_click`,
        };

        customEventTracking(body);
        logStatsigEvent(client, body.eventName, userDetails._id, {
          orgId: userDetails.organizationId,
        });
      }

      if (upgradeOffer?.dashboardUpgradeUrl) {
        window.location.assign(upgradeOffer.dashboardUpgradeUrl);

        return;
      }

      navigate(PATHS.pricingPlans);
    };

    const handleSetupCall = async () => {
      if (userDetails) {
        const body: CustomEventTrackingPayload = {
          userId: userDetails._id,
          orgId: userDetails.organizationId,
          eventName: `${trimmedPath}_setup_call_click`,
        };

        customEventTracking(body);
        logStatsigEvent(client, body.eventName, userDetails._id, {
          orgId: userDetails.organizationId,
        });
      }

      if (upgradeOffer?.setupCallUrl) {
        window.open(upgradeOffer.setupCallUrl, '_blank', 'noopener,noreferrer');
      }
    };

    if (!userDetails && location.pathname === PATHS.unAuthorized) {
      return null;
    }

    if (!userDetails) {
      return <ErrorBoundary message={USER_DETAILS_NOT_FOUND_ERROR} />;
    }

    return (
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            borderRadius: '8px',
            gap: 2,
            textAlign: 'center',
            maxWidth: '400px',
            margin: 'auto',
            backgroundColor: variableStyles.midnightIndigo,
          }}
        >
          <Typography
            variant="body1"
            style={{
              marginTop: '16px',
              lineHeight: 1.5,
            }}
          >
            {upgradeOffer
              ? `You’ve reached your free-plan invocation limit. Upgrade now and use code ${upgradeOffer.code} for ${upgradeOffer.discountPercent}% off your first month.`
              : 'To unlock this premium feature, please upgrade your plan.'}
          </Typography>

          {!isMaxPlan && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpgrade}
                style={{
                  padding: '4px 12px',
                  fontSize: '16px',
                }}
              >
                Upgrade Now
              </Button>
              {upgradeOffer?.setupCallUrl && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleSetupCall}
                  style={{
                    padding: '4px 12px',
                    fontSize: '16px',
                  }}
                >
                  Book 20-min Setup Call
                </Button>
              )}
            </>
          )}
        </Box>
      </Dialog>
    );
  },
);

export default UpgradePopup;
